import React from 'react';
import { ArrowRight, Users, Recycle, Clock, Database, Network, Lightbulb, AlertTriangle } from 'lucide-react';

const LandingPage = ({ onEnterApp }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-teal-50">
      {/* Prototype Banner */}
      <div className="bg-amber-100 border-b border-amber-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-amber-800">
          <AlertTriangle className="h-5 w-5" />
          <span className="font-medium">PROTOTYPE APPLICATION IN DEVELOPMENT</span>
          <AlertTriangle className="h-5 w-5" />
        </div>
      </div>

      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-teal-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              PropShare Network
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Revolutionizing prop sharing across theater communities through digital resource networks
            </p>
            <p className="text-lg text-gray-700 mb-10 max-w-2xl mx-auto">
              Inspired by the digitization revolution of library systems, PropShare Network creates 
              accessible pathways for artists to discover and share theatrical resources while 
              building sustainable community connections.
            </p>
            <button
              onClick={onEnterApp}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-purple-700 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg"
            >
              Enter Prototype Application
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mission Statement */}
      <section className="py-16 bg-white/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Breaking Down Barriers to Creative Resources</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Theater organizations currently face significant challenges in prop management: 
            time-intensive manual cataloging, inefficient storage tracking, limited resource 
            sharing between organizations, and barriers to discovery that prevent artists 
            from accessing the props they need. PropShare Network addresses these systemic 
            issues by creating a digital infrastructure that mirrors the transformative 
            impact libraries had when they digitized their catalogs and created 
            inter-library loan networks.
          </p>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Transformative Impact</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Recycle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Environmental Sustainability</h3>
              </div>
              <p className="text-gray-700">
                Reduces waste and resource consumption by enabling efficient sharing and reuse of 
                props across multiple organizations, decreasing the need for new prop construction 
                and preventing useful items from ending up in landfills.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Time Efficiency</h3>
              </div>
              <p className="text-gray-700">
                Dramatically reduces time spent on prop management tasks: automated digital 
                cataloging replaces manual inventory, streamlined search eliminates lengthy 
                physical searches, and efficient sharing protocols reduce acquisition time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Database className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Digital Transformation</h3>
              </div>
              <p className="text-gray-700">
                Modernizes prop management from analog to digital systems, creating searchable 
                databases, automated tracking, and data-driven insights that improve operational 
                efficiency and resource allocation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Network className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Community Building</h3>
              </div>
              <p className="text-gray-700">
                Strengthens connections between theater organizations, fostering collaboration 
                and mutual support while building a resilient network that benefits the entire 
                creative community.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Lightbulb className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Creative Access</h3>
              </div>
              <p className="text-gray-700">
                Removes barriers to creative resources, enabling artists and small organizations 
                to access props they couldn't otherwise afford or locate, democratizing access 
                to high-quality theatrical materials.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Users className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Training Efficiency</h3>
              </div>
              <p className="text-gray-700">
                Reduces training time for new staff and volunteers through intuitive digital 
                interfaces and automated processes, allowing organizations to onboard personnel 
                more quickly and effectively.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* User Roles */}
      <section className="py-16 bg-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Designed for Every Role</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-blue-900 mb-4">General Users</h3>
              <p className="text-blue-800 mb-6">
                Artists, directors, and production staff seeking props for their shows
              </p>
              <ul className="space-y-3 text-blue-700">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Search and discover props across multiple organizations</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Create and manage personal prop lists for productions</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Submit reservation requests with automated coordination</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Access recommendations and similar props</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8 border border-purple-200">
              <h3 className="text-2xl font-bold text-purple-900 mb-4">Warehouse Administrators</h3>
              <p className="text-purple-800 mb-6">
                Staff managing prop collections and organizational resources
              </p>
              <ul className="space-y-3 text-purple-700">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Digitally catalog and manage prop inventories</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Process and approve reservation requests</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Track prop availability and movement</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Generate analytics and usage reports</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-8 border border-teal-200">
              <h3 className="text-2xl font-bold text-teal-900 mb-4">Service Providers</h3>
              <p className="text-teal-800 mb-6">
                Technical administrators maintaining the network infrastructure
              </p>
              <ul className="space-y-3 text-teal-700">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Monitor system health and performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Manage cloud storage and digital asset migration</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Configure organizations and category structures</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Coordinate digitization and data cleanup processes</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Library System Inspiration */}
      <section className="py-16 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Inspired by Library System Evolution</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Just as libraries transformed from isolated collections to interconnected networks 
            through digitization and inter-library loan systems, PropShare Network applies 
            these same principles to theatrical resources. The digitization of library catalogs 
            democratized access to information, enabling researchers and students to discover 
            and access materials regardless of physical location.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            PropShare Network brings this same revolutionary accessibility to theatrical props, 
            creating a digital catalog that connects organizations and enables resource sharing 
            on a scale previously impossible. This system preserves the community-oriented 
            values of libraries while leveraging modern technology to solve longstanding 
            challenges in prop management and access.
          </p>
        </div>
      </section>

      {/* Current Challenges */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Addressing Current Challenges</h2>
          <div className="grid md:grid-cols-2 gap-8">
            
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Traditional Prop Management Issues:</h3>
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2">Manual Cataloging</h4>
                  <p className="text-red-700">Time-intensive physical inventory processes that require extensive training and are prone to errors</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2">Isolated Resources</h4>
                  <p className="text-red-700">Props stored in organizational silos, preventing broader community access and resource optimization</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2">Discovery Barriers</h4>
                  <p className="text-red-700">No systematic way for artists to find needed props across organizations, leading to unnecessary purchases</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">PropShare Network Solutions:</h3>
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">Digital Automation</h4>
                  <p className="text-green-700">Streamlined digital cataloging with image management, automated tracking, and intuitive interfaces</p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">Network Connectivity</h4>
                  <p className="text-green-700">Unified platform connecting multiple organizations for resource sharing and collaborative management</p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">Enhanced Discovery</h4>
                  <p className="text-green-700">Powerful search tools, recommendation systems, and filtering capabilities for finding exactly what's needed</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Join the Future of Prop Sharing</h2>
          <p className="text-xl mb-8 text-purple-100">
            Experience how digital transformation can revolutionize prop management 
            and community resource sharing in the theatrical arts.
          </p>
          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 mb-8">
            <p className="text-lg text-purple-100">
              <strong className="text-white">Development Status:</strong> This prototype application demonstrates 
              core functionality including digital cataloging, cross-organizational search, 
              reservation systems, and community networking features. Active development 
              continues to enhance features and expand capabilities.
            </p>
          </div>
          <button
            onClick={onEnterApp}
            className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Explore the Prototype
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            PropShare Network - Revolutionizing theatrical resource sharing through digital innovation
          </p>
          <p className="text-xs mt-2 text-gray-500">
            Prototype Application in Development - Not for Production Use
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;